import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Alert, Badge, Button, Spinner,
} from '@openedx/paragon';
// eslint-disable-next-line import/no-extraneous-dependencies
import { RiArrowLeftSLine, RiRouteLine } from '@remixicon/react';

import { usePathwayDiscover } from './usePathwayDiscover';
import { PathwayComponentsList } from './components';
import messages from './messages';

import './index.scss';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

const resolveImageUrl = (image) => {
  if (!image) { return FALLBACK_IMAGE; }
  return image.startsWith('http') ? image : `${getConfig().LMS_BASE_URL}${image}`;
};

const PathwayDiscoverDetail = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathwayId: rawId } = useParams();
  const pathwayId = decodeURIComponent(rawId || '');
  const [enrollError, setEnrollError] = useState(false);

  const {
    pathway, components, isEnrolled, enroll, enrolling, loading, error,
  } = usePathwayDiscover(pathwayId);

  if (loading && !pathway) {
    return (
      <div className="discover-pathway__state">
        <Spinner animation="border" screenReaderText={intl.formatMessage(messages.loading)} />
      </div>
    );
  }

  if (error || !pathway) {
    return (
      <div className="discover-pathway__state discover-pathway__state--error">
        {intl.formatMessage(messages.loadError)}
      </div>
    );
  }

  const imageUrl = resolveImageUrl(pathway.image);
  const componentCount = components.length;

  const handleEnroll = async () => {
    setEnrollError(false);
    try {
      await enroll();
      navigate(`/pathways/${pathway.id}`);
    } catch (err) {
      setEnrollError(true);
    }
  };

  return (
    <div className="discover-pathway">
      <Button
        variant="tertiary"
        size="sm"
        className="discover-pathway__back"
        iconBefore={RiArrowLeftSLine}
        onClick={() => navigate(-1)}
      >
        {intl.formatMessage(messages.goBack)}
      </Button>

      <div className="discover-pathway__layout">
        <div className="discover-pathway__media">
          <img src={imageUrl} alt="" className="discover-pathway__image" />
        </div>

        <div className="discover-pathway__main">
          <Badge variant="soft-secondary" className="discover-pathway__badge">
            <RiRouteLine size={14} className="mr-1" />
            {intl.formatMessage(messages.componentCount, { count: componentCount })}
          </Badge>
          <h1 className="discover-pathway__title">{pathway.name}</h1>
          {pathway.description && (
            <p className="discover-pathway__description">{pathway.description}</p>
          )}
          {pathway.isSequential && (
            <p className="discover-pathway__note text-muted">
              {intl.formatMessage(messages.sequentialNote)}
            </p>
          )}

          {enrollError && (
            <Alert variant="danger" className="discover-pathway__alert">
              {intl.formatMessage(messages.enrollError)}
            </Alert>
          )}

          {isEnrolled ? (
            <Button variant="primary" onClick={() => navigate(`/pathways/${pathway.id}`)}>
              {intl.formatMessage(messages.continue)}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleEnroll}
              disabled={enrolling}
              iconBefore={enrolling ? Spinner : undefined}
            >
              {intl.formatMessage(enrolling ? messages.enrolling : messages.enroll)}
            </Button>
          )}
        </div>
      </div>

      <PathwayComponentsList components={components} />
    </div>
  );
};

export default PathwayDiscoverDetail;
